import React, { useState, useEffect, useMemo } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Table,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Input,
  Card,
  Alert,
  Modal,
  ModalDialog,
  ModalClose
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import { BRAND } from '../brand';
import { useSetPageHeaderAction } from '../context/PageHeaderActionsContext';
import StockListCard from '../components/StockListCard';
import StockMobileList from '../components/StockMobileList';
import StockPageLayout from '../components/StockPageLayout';

const ITEMS_PER_PAGE = 10;

const Vendors = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const [newVendor, setNewVendor] = useState({
    name: '',
    mobile: '',
    email: '',
    address: ''
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', mobile: '', email: '', address: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredVendors = useMemo(() => {
    if (!searchTerm.trim()) return vendors;
    const term = searchTerm.trim().toLowerCase();
    const termNorm = term.replace(/\s/g, '').replace(/-/g, '');
    return vendors.filter((v) => {
      const name = (v.name || '').toLowerCase();
      const email = (v.email || '').toLowerCase();
      const mobile = (v.mobile || '').replace(/\s/g, '').replace(/-/g, '');
      return (
        name.includes(term) ||
        email.includes(term) ||
        (termNorm.length > 0 && mobile.includes(termNorm))
      );
    });
  }, [vendors, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredVendors.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedVendors = filteredVendors.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/vendors');
      setVendors(response.data.vendors || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleNewVendorChange = (e) => {
    setNewVendor((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAddVendor = async (e) => {
    e.preventDefault();
    setAddError('');
    if (!newVendor.name.trim()) {
      setAddError('Vendor name is required');
      return;
    }
    try {
      setAddLoading(true);
      await axiosInstance.post('/vendors', {
        name: newVendor.name.trim(),
        mobile: newVendor.mobile.trim() || undefined,
        email: newVendor.email.trim() || undefined,
        address: newVendor.address.trim() || undefined
      });
      setNewVendor({ name: '', mobile: '', email: '', address: '' });
      setAddModalOpen(false);
      fetchVendors();
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to add vendor');
    } finally {
      setAddLoading(false);
    }
  };

  const openEditModal = (v) => {
    setEditingVendor(v);
    setEditForm({
      name: v.name || '',
      mobile: v.mobile || '',
      email: v.email || '',
      address: v.address || ''
    });
    setEditError('');
    setEditModalOpen(true);
  };

  const handleEditFormChange = (e) => {
    setEditForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const headerAction = useMemo(
    () => (
      <Button
        size="sm"
        startDecorator={<AddIcon />}
        onClick={() => setAddModalOpen(true)}
        sx={{
          backgroundColor: 'primary.500',
          '&:hover': { backgroundColor: 'primary.600' }
        }}
      >
        Add Vendor
      </Button>
    ),
    []
  );

  useSetPageHeaderAction(headerAction);

  const handleUpdateVendor = async (e) => {
    e.preventDefault();
    if (!editingVendor) return;
    setEditError('');
    if (!editForm.name.trim()) {
      setEditError('Vendor name is required');
      return;
    }
    try {
      setEditLoading(true);
      await axiosInstance.put(`/vendors/${editingVendor.id}`, {
        name: editForm.name.trim(),
        mobile: editForm.mobile.trim() || undefined,
        email: editForm.email.trim() || undefined,
        address: editForm.address.trim() || undefined
      });
      setEditModalOpen(false);
      setEditingVendor(null);
      fetchVendors();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update vendor');
    } finally {
      setEditLoading(false);
    }
  };

  const listEmptyMessage = vendors.length === 0
    ? 'No vendors yet. Add one to use when adding materials or disposables.'
    : 'No vendors match your search.';

  const paginationBar = (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
      <Typography level="body-md">
        {filteredVendors.length === 0
          ? 'No vendors to show'
          : `Showing ${startIndex + 1}-${Math.min(startIndex + ITEMS_PER_PAGE, filteredVendors.length)} of ${filteredVendors.length} vendors`}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          disabled={currentPage === 1 || filteredVendors.length === 0}
          onClick={() => setCurrentPage((p) => p - 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Previous
        </Button>
        <Button
          variant="outlined"
          disabled={currentPage >= totalPages || filteredVendors.length === 0}
          onClick={() => setCurrentPage((p) => p + 1)}
          sx={{
            borderColor: 'primary.500',
            color: 'primary.500',
            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.1)' }
          }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <StockPageLayout
        scrollListAlways
        error={error && (
          <Alert color="danger" onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        loading={loading}
        search={(
          <Card sx={{ mb: { xs: 0, md: 0 }, p: 2 }}>
            <FormControl sx={{ maxWidth: 400 }}>
              <FormLabel>Search by name, email or mobile</FormLabel>
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search vendors..."
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
          </Card>
        )}
        pagination={paginationBar}
      >
        <StockMobileList
          empty={paginatedVendors.length === 0}
          emptyMessage={listEmptyMessage}
        >
          {paginatedVendors.map((v) => (
            <StockListCard
              key={v.id}
              title={v.name}
              fields={[
                { label: 'Mobile', value: v.mobile || '—' },
                { label: 'Email', value: v.email || '—' },
                { label: 'Address', value: v.address || '—' },
                { label: 'Created By', value: v.created_by || '—' },
                {
                  label: 'Actions',
                  value: (
                    <Button
                      size="sm"
                      variant="outlined"
                      color="neutral"
                      onClick={() => openEditModal(v)}
                      sx={{
                        borderColor: 'primary.500',
                        color: 'primary.500',
                        '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.08)' }
                      }}
                    >
                      Edit
                    </Button>
                  )
                }
              ]}
            />
          ))}
        </StockMobileList>

        <Card sx={{ display: { xs: 'none', md: 'block' } }}>
          <Box
            sx={{
              overflowX: 'auto',
              '& thead th': {
                backgroundColor: 'background.body',
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: 'bold',
                borderBottom: '2px solid',
                borderColor: 'primary.500',
                position: 'sticky',
                top: 0,
                zIndex: 1
              },
              '& tbody td': {
                padding: '12px 16px',
                borderBottom: '1px solid #eee'
              },
              '& tbody tr:nth-of-type(even)': {
                backgroundColor: '#f9f9f9'
              },
              '& tbody tr:hover': {
                backgroundColor: 'primary.50'
              },
              '& td': {
                wordWrap: 'break-word',
                whiteSpace: 'normal'
              }
            }}
          >
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '16%' }}>Name</th>
                  <th style={{ width: '12%' }}>Mobile</th>
                  <th style={{ width: '16%' }}>Email</th>
                  <th style={{ width: '32%' }}>Address</th>
                  <th style={{ width: '14%' }}>Created By</th>
                  <th style={{ width: '10%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVendors.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>
                      {listEmptyMessage}
                    </td>
                  </tr>
                ) : (
                  paginatedVendors.map((v) => (
                    <tr key={v.id}>
                      <td style={{ color: BRAND.colors.navy, fontWeight: 'bold' }}>{v.name}</td>
                      <td>{v.mobile || '—'}</td>
                      <td>{v.email || '—'}</td>
                      <td>{v.address || '—'}</td>
                      <td>{v.created_by || '—'}</td>
                      <td>
                        <Button
                          size="sm"
                          variant="outlined"
                          color="neutral"
                          onClick={() => openEditModal(v)}
                          sx={{
                            borderColor: 'primary.500',
                            color: 'primary.500',
                            '&:hover': { borderColor: 'primary.600', backgroundColor: 'rgba(29, 27, 77, 0.08)' }
                          }}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Box>
        </Card>
      </StockPageLayout>

      <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)}>
        <ModalDialog sx={{ maxWidth: 400 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>
            Add Vendor
          </Typography>
          {addError && (
            <Alert color="danger" sx={{ mb: 2 }} onClose={() => setAddError('')}>
              {addError}
            </Alert>
          )}
          <form onSubmit={handleAddVendor}>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Name</FormLabel>
              <Input
                name="name"
                value={newVendor.name}
                onChange={handleNewVendorChange}
                required
                placeholder="Vendor name"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Mobile</FormLabel>
              <Input
                name="mobile"
                value={newVendor.mobile}
                onChange={handleNewVendorChange}
                placeholder="Mobile number"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={newVendor.email}
                onChange={handleNewVendorChange}
                placeholder="Email"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Address</FormLabel>
              <Input
                name="address"
                value={newVendor.address}
                onChange={handleNewVendorChange}
                placeholder="Address"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <Button
              type="submit"
              loading={addLoading}
              fullWidth
              sx={{
                backgroundColor: 'primary.500',
                '&:hover': { backgroundColor: 'primary.600' }
              }}
            >
              Add Vendor
            </Button>
          </form>
        </ModalDialog>
      </Modal>

      <Modal open={editModalOpen} onClose={() => { setEditModalOpen(false); setEditingVendor(null); }}>
        <ModalDialog sx={{ maxWidth: 400 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>
            Edit Vendor
          </Typography>
          {editError && (
            <Alert color="danger" sx={{ mb: 2 }} onClose={() => setEditError('')}>
              {editError}
            </Alert>
          )}
          <form onSubmit={handleUpdateVendor}>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Name</FormLabel>
              <Input
                name="name"
                value={editForm.name}
                onChange={handleEditFormChange}
                required
                placeholder="Vendor name"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Mobile</FormLabel>
              <Input
                name="mobile"
                value={editForm.mobile}
                onChange={handleEditFormChange}
                placeholder="Mobile number"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={editForm.email}
                onChange={handleEditFormChange}
                placeholder="Email"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Address</FormLabel>
              <Input
                name="address"
                value={editForm.address}
                onChange={handleEditFormChange}
                placeholder="Address"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <Button
              type="submit"
              loading={editLoading}
              fullWidth
              sx={{
                backgroundColor: 'primary.500',
                '&:hover': { backgroundColor: 'primary.600' }
              }}
            >
              Save
            </Button>
          </form>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default Vendors;
